// src/pages/Roadmap/PeerTakes.jsx
// STATUS: active | UPDATED: 2026-09-10
// LOG:
//   2026-09-10  New. Hold the mic, say how you do it, let your floor hear it.
// Peer teaching, on the step you are already looking at.
//
// VOICE FIRST, AND THAT IS NOT A PREFERENCE.
//
// Jazz dictated 52 of his messages on 2026-09-10 rather than typing them. His
// method has always travelled by somebody saying it out loud next to you, and a
// text box produces careful written sentences nobody actually says to a
// customer. Hold the mic and talk, exactly as the coach works.
//
// THE ORDER OF THE BUTTONS IS THE ARGUMENT.
//
// Record, then "what do you think", then publish. The middle step is optional
// and never blocks the third. It is there because a floor sharing its own
// wording is only safe while somebody knows which variations Jazz accepts and
// which he ruled out, and now something does.

import { useEffect, useState } from 'react'
import { Box, Button, HStack, Spinner, Text, Textarea, VStack, useToast } from '@chakra-ui/react'
import { Mic, Send, Sparkles, Square } from 'lucide-react'
import { useDictation } from '../../hooks/useDictation'
import { createTake, reviewTake, setTakeStatus, myTakes, floorTakes, updateTakeBody } from '../../lib/peerTakes'
import { firstNameFor } from '../../lib/names.js'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'

export default function PeerTakes({ stepNumber, userId, dealershipId }) {
  const [draft, setDraft] = useState('')
  const [mine, setMine] = useState([])
  const [floor, setFloor] = useState([])
  const [saving, setSaving] = useState(false)
  const [reviewing, setReviewing] = useState(null)
  // The roster, only so a take can be signed with a name rather than a uuid.
  // RLS scopes this to the caller's own dealership.
  const [teammates, setTeammates] = useState([])
  // A manager can take a teammate's take down. The RLS has always allowed it,
  // and without a control for it the first thing anybody asks about peer
  // teaching, "what if somebody puts something wrong out there", has no answer.
  const { role } = useAuth()
  const canTakeDown = role === 'manager' || role === 'owner' || role === 'admin'
  const toast = useToast()

  const dictation = useDictation({
    onText: (text) => setDraft((prev) => (prev.trim() ? `${prev.trim()} ${text}` : text)),
  })

  const refresh = async () => {
    if (!userId) return
    const [{ data: m }, { data: f }] = await Promise.all([
      myTakes(userId, stepNumber),
      floorTakes(dealershipId, stepNumber, userId),
    ])
    setMine(m)
    setFloor(f)
  }

  useEffect(() => {
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, dealershipId, stepNumber])

  useEffect(() => {
    if (!dealershipId) return
    supabase
      .from('users')
      .select('id, display_name, first_name, email')
      .eq('dealership_id', dealershipId)
      .then(({ data }) => setTeammates(data || []))
  }, [dealershipId])

  // Jazz and Tyler have no floor to share with. A dead control is worse than
  // no control.
  if (!userId || !dealershipId) return null

  const nameOf = (id) => {
    const t = teammates.find((x) => x.id === id)
    return t ? firstNameFor(t, t) : 'A teammate'
  }

  const save = async () => {
    const text = draft.trim()
    if (text.length < 20) {
      toast({ status: 'info', title: 'Give it a bit more', description: 'A sentence or two is enough.' })
      return
    }
    setSaving(true)
    const { error } = await createTake({ dealershipId, userId, stepNumber, body: text })
    setSaving(false)
    if (error) {
      toast({ status: 'error', title: 'That did not save' })
      return
    }
    setDraft('')
    await refresh()
  }

  const askCoach = async (id) => {
    setReviewing(id)
    const { note } = await reviewTake(id)
    setReviewing(null)
    await refresh()
    if (!note) toast({ status: 'info', title: 'Nothing came back', description: 'Publish it whenever you like.' })
  }

  const publish = async (id) => {
    const { error } = await setTakeStatus(id, 'published')
    if (error) {
      toast({ status: 'error', title: 'That did not save' })
      return
    }
    await refresh()
    toast({ status: 'success', title: 'Your floor can see it now' })
  }

  const withdraw = async (id) => {
    await setTakeStatus(id, 'hidden')
    await refresh()
  }

  // A manager removing somebody else's. Quiet on purpose: the conversation that
  // should follow is one they have in person, not a notification.
  const takeDown = async (id) => {
    const { error } = await setTakeStatus(id, 'hidden')
    if (error) {
      toast({ status: 'error', title: 'That did not save' })
      return
    }
    await refresh()
    toast({ status: 'success', title: 'Taken down', description: 'Worth telling them why yourself.' })
  }

  const recording = dictation.state === 'recording'
  const working = dictation.state === 'working' || dictation.state === 'starting'

  return (
    <Box borderTop="1px solid" borderColor="line" pt={5} mt={5}>
      <Text fontWeight={600} mb={1} letterSpacing="-0.01em">
        How you say it
      </Text>
      <Text fontSize="body" color="inkMuted" mb={4} lineHeight={1.6}>
        Record how you actually run this step and your floor can hear it. Ninety seconds is plenty. Nobody
        sees it until you say so.
      </Text>

      <VStack align="stretch" spacing={3} mb={floor.length > 0 || mine.length > 0 ? 7 : 0}>
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={dictation.supported ? 'Hold the mic and talk, or type it' : 'Type how you run this step'}
          _placeholder={{ color: 'inkDim' }}
          rows={3}
          resize="none"
          fontSize="body"
        />
        <HStack spacing={2} flexWrap="wrap">
          {dictation.supported && (
            <Button
              size="sm"
              leftIcon={working ? <Spinner size="xs" /> : recording ? <Square size={14} /> : <Mic size={15} />}
              variant={recording ? 'solid' : 'outline'}
              bg={recording ? 'accent.500' : undefined}
              color={recording ? 'onInk' : undefined}
              _hover={recording ? { bg: 'accent.500' } : undefined}
              isDisabled={working}
              onPointerDown={(e) => {
                e.preventDefault()
                e.currentTarget.setPointerCapture?.(e.pointerId)
                dictation.pressStart()
              }}
              onPointerUp={dictation.pressEnd}
              onPointerCancel={dictation.pressEnd}
            >
              {recording ? `Recording ${dictation.seconds}s` : 'Hold to talk'}
            </Button>
          )}
          <Button size="sm" leftIcon={<Send size={15} />} onClick={save} isLoading={saving} isDisabled={draft.trim().length < 20}>
            Save it
          </Button>
        </HStack>
        {dictation.guidance && (
          <Text fontSize="sm" color="inkDim">
            {dictation.guidance}
          </Text>
        )}
      </VStack>

      {mine.length > 0 && (
        <VStack align="stretch" spacing={4} mb={floor.length > 0 ? 7 : 0}>
          <Text fontSize="sm" textTransform="uppercase" letterSpacing="0.08em" color="inkDim">
            Yours
          </Text>
          {mine.filter((t) => t.status !== 'hidden').map((t) => (
            <Box key={t.id} borderLeft="2px solid" borderColor="line" pl={4}>
              <Textarea
                defaultValue={t.body}
                onBlur={(e) => { if (e.target.value.trim() !== t.body) updateTakeBody(t.id, e.target.value) }}
                variant="unstyled"
                _placeholder={{ color: 'inkDim' }}
                rows={3}
                resize="none"
                fontSize="body"
                lineHeight={1.7}
              />
              {t.review_note && (
                <Box bg="surface" borderRadius="md" p={3} mt={2}>
                  <HStack spacing={2} mb={1}>
                    <Sparkles size={13} />
                    <Text fontSize="sm" fontWeight={600}>
                      Between you and the coach
                    </Text>
                  </HStack>
                  <Text fontSize="body" lineHeight={1.65} whiteSpace="pre-wrap">
                    {t.review_note}
                  </Text>
                </Box>
              )}
              <HStack spacing={2} mt={3} flexWrap="wrap">
                {t.status === 'draft' && (
                  <>
                    <Button size="sm" variant="ghost" leftIcon={<Sparkles size={14} />} onClick={() => askCoach(t.id)} isLoading={reviewing === t.id}>
                      {t.review_note ? 'Look again' : 'What do you think?'}
                    </Button>
                    <Button size="sm" onClick={() => publish(t.id)}>
                      Share with my floor
                    </Button>
                  </>
                )}
                {t.status === 'published' && (
                  <>
                    <Text fontSize="sm" color="inkDim" mr={1}>
                      Your floor can see this
                    </Text>
                    <Button size="sm" variant="ghost" onClick={() => withdraw(t.id)}>
                      Take it down
                    </Button>
                  </>
                )}
              </HStack>
            </Box>
          ))}
        </VStack>
      )}

      {floor.length > 0 && (
        <VStack align="stretch" spacing={4}>
          <Text fontSize="sm" textTransform="uppercase" letterSpacing="0.08em" color="inkDim">
            How your floor does it
          </Text>
          {floor.map((t) => (
            <Box key={t.id} borderLeft="2px solid" borderColor="accent.500" pl={4}>
              <Text fontSize="body" lineHeight={1.7} whiteSpace="pre-wrap">
                {t.body}
              </Text>
              <HStack justify="space-between" mt={2} spacing={3}>
                <Text fontSize="sm" color="inkDim">
                  {nameOf(t.author_id)}
                </Text>
                {canTakeDown && (
                  <Button size="xs" variant="ghost" color="inkDim" onClick={() => takeDown(t.id)}>
                    Take down
                  </Button>
                )}
              </HStack>
            </Box>
          ))}
        </VStack>
      )}
    </Box>
  )
}
